import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Banknote, ChevronRight, HardHat, Mic, ShieldCheck } from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { hlaskaZChyby } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { Btn, Chyba, Input, Pole } from '../components/ui.js';

/**
 * Přihlášení — zároveň první marketingový dotyk: levý (navy) panel říká, čeho
 * s Helfrem dosáhnete (deník za minutu, z nálezu faktura, peníze pod
 * kontrolou), pravý panel je čistý formulář. Chybové stavy česky (z API).
 */
export function LoginPage() {
  const { me, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [heslo, setHeslo] = useState('');
  const [zobrazitHeslo, setZobrazitHeslo] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const [odesilam, setOdesilam] = useState(false);

  const cil = (location.state as { od?: string } | null)?.od ?? '/';
  if (me) return <Navigate to={cil} replace />;

  const prihlas = async (mail: string, pass: string) => {
    setChyba(null);
    setOdesilam(true);
    try {
      await login(mail, pass);
      void navigate(cil, { replace: true });
    } catch (err) {
      setChyba(hlaskaZChyby(err));
    } finally {
      setOdesilam(false);
    }
  };

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    void prihlas(email, heslo);
  };

  return (
    <main className="min-h-screen bg-bg lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/* Proč Helfr — hodnota dřív než formulář. */}
      <section className="flex flex-col justify-between gap-10 bg-primary p-6 text-white lg:min-h-screen lg:p-12">
        <div className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-text-heading">
            <HardHat size={20} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold tracking-widest uppercase">{APP_NAME}</span>
        </div>

        <div className="max-w-xl">
          <h1 className="text-3xl leading-tight font-bold text-balance text-white lg:text-4xl">
            Ať se žádná vícepráce neztratí.
          </h1>
          <p className="mt-3 max-w-prose text-lg text-primary-tint">
            Helfr je stavební deník a hlídač víceprací — dovede fotku z terénu až k zaplacené
            faktuře.
          </p>

          {/* Tři sliby — na telefonu skryté, ať je formulář hned pod palcem. */}
          <ul className="mt-8 hidden flex-col gap-5 lg:flex">
            <Slib ikona={<Mic size={20} strokeWidth={1.75} />} titulek="Deník za minutu">
              Mistr den nadiktuje, přidá fotky a večer podepíše. Žádný papír, žádné dopisování.
            </Slib>
            <Slib ikona={<Banknote size={20} strokeWidth={1.75} />} titulek="Z nálezu faktura">
              Co terén vyfotí, kancelář ocení, nechá schválit a vyfakturuje. Nic nezapadne.
            </Slib>
            <Slib
              ikona={<ShieldCheck size={20} strokeWidth={1.75} />}
              titulek="Peníze pod kontrolou"
            >
              Hlídače vás varují, dokud se s rozpočtem dá něco dělat — ne až po fakturaci.
            </Slib>
          </ul>
        </div>

        <p className="text-sm text-primary-tint">
          Jste tu poprvé?{' '}
          <Link to="/registrace" className="font-semibold text-white underline underline-offset-2">
            Založte firmu zdarma
          </Link>
        </p>

        {/* Cesta peněz — konkrétní slib, kam až to dotáhnete. */}
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-semibold tracking-wider uppercase">
          <span className="text-primary-tint">Nález</span>
          <Sipka />
          <span className="text-primary-tint">Změna</span>
          <Sipka />
          <span className="text-primary-tint">Dodatek</span>
          <Sipka />
          <span className="text-accent">Zaplacená faktura</span>
        </p>
      </section>

      {/* Formulář — čistý, bez marketingu. */}
      <section className="flex flex-col items-center justify-center gap-4 p-6 lg:p-12">
        <form
          onSubmit={odeslat}
          className="flex w-full max-w-md flex-col gap-6 rounded-xl border border-border bg-surface p-8 shadow-md"
        >
          <div>
            <h2 className="text-2xl font-bold">Přihlášení</h2>
            <p className="mt-1 text-base text-text-secondary">Vítejte zpátky na stavbě.</p>
          </div>
          <Pole popisek="E-mail" povinne>
            <Input
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Pole>
          <Pole popisek="Heslo" povinne>
            {/* Zobrazit heslo (prevence chyb: 50+ nechce psát naslepo). */}
            <div className="relative">
              <Input
                type={zobrazitHeslo ? 'text' : 'password'}
                name="heslo"
                autoComplete="current-password"
                required
                className="pr-24"
                value={heslo}
                onChange={(e) => setHeslo(e.target.value)}
              />
              <button
                type="button"
                aria-pressed={zobrazitHeslo}
                onClick={() => setZobrazitHeslo((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-sm font-semibold text-primary hover:bg-primary-tint"
              >
                {zobrazitHeslo ? 'Skrýt' : 'Zobrazit'}
              </button>
            </div>
          </Pole>
          <Chyba>{chyba}</Chyba>
          <Btn type="submit" className="w-full" disabled={odesilam}>
            {odesilam ? 'Přihlašuji…' : 'Přihlásit se'}
          </Btn>
          <p className="text-center text-sm text-text-secondary">
            Zapomenuté heslo vám obnoví správce firmy (Uživatelé).
          </p>
        </form>

        {/* DOČASNÉ (jen pro testování dema) — rychlé přihlášení jedním klikem.
            Před ostrým nasazením celý blok DemoPrihlaseni smazat. */}
        <DemoPrihlaseni onVyber={prihlas} odesilam={odesilam} />
      </section>
    </main>
  );
}

/** Jeden slib na navy panelu: ikona v chipu + tučný titulek + jedna věta. */
function Slib({
  ikona,
  titulek,
  children,
}: {
  ikona: ReactNode;
  titulek: string;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-accent">
        {ikona}
      </span>
      <span className="min-w-0">
        <span className="block text-base font-bold">{titulek}</span>
        <span className="block max-w-prose text-base text-primary-tint">{children}</span>
      </span>
    </li>
  );
}

function Sipka() {
  return <ChevronRight size={14} strokeWidth={2} aria-hidden="true" className="text-white/50" />;
}

/**
 * DOČASNÉ: rychlé přihlášení demo uživateli (heslo shodné pro všechny). Celý
 * blok je určen ke smazání před ostrým provozem — proto samostatná komponenta.
 */
const DEMO_UCTY = [
  { email: 'petra.pripravarova@demostavby.cz', jmeno: 'Petra', role: 'přípravář (kancelář)' },
  { email: 'milan.mistr@demostavby.cz', jmeno: 'Milan', role: 'mistr (terén)' },
  { email: 'dusan.dvorak@demostavby.cz', jmeno: 'Dušan', role: 'stavbyvedoucí' },
  { email: 'radim.reditel@demostavby.cz', jmeno: 'Radim', role: 'ředitel' },
];
const DEMO_HESLO = 'Heslo.123';

function DemoPrihlaseni({
  onVyber,
  odesilam,
}: {
  onVyber: (email: string, heslo: string) => void | Promise<void>;
  odesilam: boolean;
}) {
  return (
    <div className="flex w-full max-w-md flex-col gap-2 rounded-xl border border-dashed border-warning-border bg-warning-bg p-4">
      <p className="text-sm font-semibold text-warning-text">
        Demo — rychlé přihlášení (dočasné, jen pro testování)
      </p>
      <div className="grid grid-cols-2 gap-2">
        {DEMO_UCTY.map((u) => (
          <button
            key={u.email}
            type="button"
            disabled={odesilam}
            onClick={() => void onVyber(u.email, DEMO_HESLO)}
            className="flex min-h-12 flex-col items-start justify-center rounded-lg border border-border bg-surface px-3 py-1 text-left transition-colors duration-100 hover:bg-bg disabled:opacity-60"
          >
            <span className="text-base font-bold">{u.jmeno}</span>
            <span className="text-sm text-text-secondary">{u.role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
